import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "app/components/button";
import { CardBody } from "app/components/card-body";
import { CardFooter } from "app/components/card-footer";
import { CardHeader } from "app/components/card-header";
import { Checkbox } from "app/components/checkbox";
import { Column } from "app/components/column";
import { Container } from "app/components/container";
import { DFlexResponsive } from "app/components/d-flex-responsive";
import { Input } from "app/components/input";
import { Row } from "app/components/row";
import { useEmpresa, useResourcesContext } from "app/contexts";
import {
  celularRegex,
  correoRegex,
  dniRegex,
  nombrePropioRegex,
  rtnRegex,
  telefonoRegex,
} from "app/utils/regexps";

import toast from "react-hot-toast";
import { ConditionalButton } from "app/components/buttons";
import { RouteError } from "app/components/route-error";

import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select as MUISelect,
} from "@mui/material";
import { Option } from "app/components/option";
import { Select } from "app/components/select";

const initialState = {
  encargado: {
    genero_id: 0,
    encargado_nombre_completo: "",
    encargado_identificacion: "",
    encargado_rtn: "",
    encargado_telefono: "",
    encargado_celular: "",
    encargado_correo_electronico: "",
    cargos: [],
  },
  error: null,
};

export function EncargadoForm() {
  const params = useParams();
  const navigate = useNavigate();

  const { cargos, generos } = useResourcesContext();

  const { addEncargado, getEncargado, updateEncargado } = useEmpresa();

  const [state, setState] = useState(initialState);

  useEffect(() => {
    try {
      if (!!params.index) {
        const encargado = getEncargado(parseInt(params.index));

        setState((state) => ({ ...state, encargado }));
      }
    } catch (error) {
      setState((state) => ({ ...state, error }));
    }
  }, [params.index, getEncargado]);

  function handleSubmit(e) {
    e.preventDefault();

    try {
      if (!!params.index) {
        const updated = updateEncargado(
          parseInt(params.index),
          state.encargado
        );

        if (!!updated) {
          toast.success("¡Encargado actualizado!");

          return navigate("../");
        }
      } else {
        const added = addEncargado(state.encargado);

        if (!!added) {
          toast.success("¡Encargado agregado!");

          return navigate("../");
        }
      }
    } catch (error) {
      toast.error(error.message);
    }
  }

  if (!!state.error) {
    return <RouteError message={state.error.message} navigateTo="../" />;
  }

  return (
    <div className="col-sm-12">
      <form className="card" onSubmit={handleSubmit}>
        <CardHeader
          header={`${!!params.index ? "Editar" : "Agregar"} encargado`}
        />

        <CardBody>
          <Container>
            <Row>
              <Column xl={6} xxl={6}>
                <Container>
                  <h5>
                    <strong>Datos personales</strong>
                  </h5>

                  <Row>
                    <Column>
                      <FormControl fullWidth required>
                        <InputLabel id="cargos-label">Cargos</InputLabel>

                        <MUISelect
                          labelId="cargos-label"
                          multiple
                          value={state.encargado.cargos}
                          onChange={(e) =>
                            setState((state) => ({
                              ...state,
                              encargado: {
                                ...state.encargado,
                                cargos: e.target.value,
                              },
                            }))
                          }
                          input={<OutlinedInput label="Chip" />}
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {selected.map((value) => {
                                const cargo = cargos.find(
                                  (cargo) => cargo.cargo_id === value
                                );

                                return (
                                  <Chip
                                    key={value}
                                    label={cargo.cargo_nombre}
                                  />
                                );
                              })}
                            </Box>
                          )}
                        >
                          {cargos.map((cargo) => (
                            <MenuItem
                              key={cargo.cargo_id}
                              value={cargo.cargo_id}
                            >
                              <Checkbox
                                checked={
                                  !!state.encargado.cargos.find(
                                    (cargo_id) => cargo_id === cargo.cargo_id
                                  )
                                }
                              />

                              <ListItemText primary={cargo.cargo_nombre} />
                            </MenuItem>
                          ))}
                        </MUISelect>
                      </FormControl>
                    </Column>

                    <Column>
                      <Select
                        placeholder="Género"
                        value={state.encargado.genero_id}
                        onChange={(e) =>
                          setState((state) => ({
                            ...state,
                            encargado: {
                              ...state.encargado,
                              genero_id: parseInt(e.target.value),
                            },
                          }))
                        }
                      >
                        {generos.map((genero) => (
                          <Option
                            key={genero.genero_id}
                            value={genero.genero_id}
                            title={genero.genero_nombre}
                          />
                        ))}
                      </Select>
                    </Column>

                    <Column>
                      <Input
                        placeholder="Nombre completo"
                        value={state.encargado.encargado_nombre_completo}
                        onChange={(e) =>
                          setState((state) => ({
                            ...state,
                            encargado: {
                              ...state.encargado,
                              encargado_nombre_completo:
                                e.target.value.trimStart(),
                            },
                          }))
                        }
                        pattern={nombrePropioRegex.source}
                        title="p. ej. José María Martínez, al menos un nombre y un apellido, nombres propios se escriben con letra mayúscula al principio, sin números ni caracteres especiales"
                      />
                    </Column>

                    <Column>
                      <Input
                        placeholder="Número de cédula, carnet de residente (con guiones) o pasaporte"
                        value={state.encargado.encargado_identificacion}
                        pattern={dniRegex.source}
                        title="Ej. 0101198700123, 01-0101-1987-00123 o C041957"
                        onChange={(e) =>
                          setState((state) => ({
                            ...state,
                            encargado: {
                              ...state.encargado,
                              encargado_identificacion: e.target.value,
                            },
                          }))
                        }
                      />
                    </Column>

                    <Column>
                      <Input
                        placeholder="Número de RTN (requerido)"
                        value={state.encargado.encargado_rtn}
                        pattern={rtnRegex.source}
                        title="Ej. 01012000123456"
                        onChange={(e) =>
                          setState((state) => ({
                            ...state,
                            encargado: {
                              ...state.encargado,
                              encargado_rtn: e.target.value,
                            },
                          }))
                        }
                        helpText={
                          <a
                            href="https://www.sar.gob.hn/consulta-rtn/"
                            target="_blank"
                            rel="noreferrer"
                          >
                            ¿No sabe el número de RTN? Consulte el RTN en el
                            sitio web del SAR (clic aquí)
                          </a>
                        }
                      />
                    </Column>
                  </Row>
                </Container>
              </Column>

              <Column xl={6} xxl={6}>
                <Container>
                  <h5>
                    <strong>Contactos</strong>
                  </h5>

                  <Row>
                    <Column>
                      <Input
                        placeholder="Número de teléfono fijo (opcional)"
                        value={state.encargado.encargado_telefono}
                        onChange={(e) =>
                          setState((state) => ({
                            ...state,
                            encargado: {
                              ...state.encargado,
                              encargado_telefono: e.target.value,
                            },
                          }))
                        }
                        pattern={telefonoRegex.source}
                        title="p. ej. 2442-0102"
                        required={false}
                      />
                    </Column>

                    <Column>
                      <Input
                        placeholder="Número de celular (requerido)"
                        value={state.encargado.encargado_celular}
                        onChange={(e) =>
                          setState((state) => ({
                            ...state,
                            encargado: {
                              ...state.encargado,
                              encargado_celular: e.target.value,
                            },
                          }))
                        }
                        pattern={celularRegex.source}
                        title="p. ej. 9898-3214 o 3336-8080"
                      />
                    </Column>

                    <Column>
                      <Input
                        placeholder="Dirección de correo electrónico (opcional)"
                        value={state.encargado.encargado_correo_electronico}
                        onChange={(e) =>
                          setState((state) => ({
                            ...state,
                            encargado: {
                              ...state.encargado,
                              encargado_correo_electronico: e.target.value,
                            },
                          }))
                        }
                        required={false}
                        title="p. ej. usuario@email.com"
                        pattern={correoRegex.source}
                      />
                    </Column>
                  </Row>
                </Container>
              </Column>
            </Row>
          </Container>
        </CardBody>

        <CardFooter>
          <DFlexResponsive justifyContent="end">
            <ConditionalButton
              type="submit"
              condition={!params.index}
              titleWhenTrue="Agregar"
              titleWhenFalse="Actualizar"
            />

            <Button
              color="secondary"
              outline
              title="Cancelar"
              onClick={() => navigate("../")}
            />
          </DFlexResponsive>
        </CardFooter>
      </form>
    </div>
  );
}
